import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import {
  Task,
  TaskStatus,
} from '@models/task.model';
import { Crew } from '@models/crew.model';
import { Organization } from '@models/organization.model';
import { Event } from '@models/event.model';
import {
  findPrecedentConflicts,
  describePrecedentConflicts,
} from '@utils/precedentConflicts';
import { useCreateTask, useUpdateTask, useCreateSubtask } from '@hooks/api';
import { useBudget } from '@hooks/api/useBudget';
import { graphQlClient } from '@config/graphqlClient';
import TasksGraphQL from '@graphql/task.queries';
import { BudgetRequest, BudgetRequestStatus } from '@models/budget.model';
import { TaskModalMode, TaskFormValues, DocumentObject } from './types';
import {
  GeneralTab,
  AssignmentTab,
  InventoryTab,
  DocumentsTab,
  DependenciesTab,
  ActivityTab,
  BudgetTab,
  DateConflictModal,
} from './components';
import { Tab } from '@components/atoms/Tab';
import { validationTaskSchema } from './validationSchema';
import {
  formatDateForRequest,
  formatDateForSubmission,
} from '@utils/formatDate';
import { parseTaskDate, formatTaskDateForBackend } from '@utils/dateOnlyParser';
import { getAncestorTaskIds } from '@utils/taskHierarchy';
import { SliderModal } from '@components/molecules/SlideOver';
import { Warning } from '@mui/icons-material';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { useFeatureAccess } from '@utils/featureFlag';
import { roleRank, TEMPLATE_ACTIONS_MIN_RANK } from '@constants/roleHierarchy';
import { ImportTaskTemplateModal } from '@components/organisms/ImportTaskTemplateModal';
import {
  DelegatedTaskModal,
  DelegatedTaskData,
} from '@pages/schedule/components/DelegatedTaskModal';
import {
  PrecedentConflictModal,
  PrecedentConflict,
} from '@pages/schedule/components/PrecedentConflictModal';
import {
  SubtaskOutOfRangeModal,
  SubtaskOutOfRangeData,
} from '@pages/schedule/components/SubtaskOutOfRangeModal';

interface CreateEditTaskModalProps {
  /**
   * Modal mode - determines behavior:
   * - 'task': Standard task creation/editing. `task` prop is the task to edit (null = create new)
   * - 'subtask': Subtask creation. `task` prop is the parent task (required)
   * @default 'task'
   */
  mode?: TaskModalMode;
  isOpen: boolean;
  onClose: () => void;
  task?: Task | null;
  tasks?: Task[];
  successTask: () => void;
  events?: Event[];
  crews?: Crew[];
  organizations?: Organization[];
  project?: any;
  projectId?: string;
  onCreateSubtaskClick?: (parentTask: Task) => void;
  initialTab?: string;
  initialInventorySubTab?: string;
}

// Re-export TaskFormValues for backward compatibility
export type CreateEditTaskValuesProps = TaskFormValues;

// Maps every validated field to the tab that renders it, so a failed submit can
// jump the user to the tab holding the first error instead of silently doing
// nothing. Keep in sync with `getTabErrors` and `tabItems` below.
const FIELD_TAB_MAP: Record<string, string> = {
  name: '0',
  description: '0',
  taskType: '0',
  otherType: '0',
  startTime: '0',
  endTime: '0',
  budget: '0',
  delegateOrganizationId: '1',
  crewIds: '1',
  manHours: '1',
  timeConstraint: '1',
  typeOfTimeConstraint: '1',
  superintendentId: '1',
  note: '1',
  materials: '2',
  equipments: '2',
  documents: '3',
  presedentTaskId: '4',
  presedentEventId: '4',
};

const TAB_LABELS: Record<string, string> = {
  '0': 'General',
  '1': 'Assignment',
  '2': 'Inventory',
  '3': 'Documents',
  '4': 'Dependencies',
};

const TAB_ORDER = ['0', '1', '2', '3', '4'];

export const CreateEditTaskModal: React.FC<CreateEditTaskModalProps> = ({
  mode = 'task',
  isOpen,
  task,
  project,
  onClose,
  projectId,
  successTask,
  tasks = [],
  events = [],
  onCreateSubtaskClick,
  initialTab,
  initialInventorySubTab,
}) => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('0');

  // Determine task context based on mode:
  // - In 'task' mode: task is the task to edit (null = create new task)
  // - In 'subtask' mode: task is the parent task (required for subtask creation)
  const parentTask = mode === 'subtask' ? task : null;
  const taskToEdit = mode === 'task' ? task : null;
  const [selectedCrews, setSelectedCrews] = useState<Crew[]>([]);
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);
  const [delegationType, setDelegationType] = useState<
    'crew' | 'subcontractor'
  >('crew');

  // Document handling states
  const [documents, setDocuments] = useState<DocumentObject[]>([]); // Existing documents from backend
  const [deleteDocuments, setDeleteDocuments] = useState<string[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([]); // New files to upload

  // Dependencies handling states
  const [preseding, setPreseding] = useState<string[]>([]);
  const [presedingEvent, setPresedingEvent] = useState<string[]>([]);

  // Subtasks handling states
  const [subtasksToAdd, setSubtasksToAdd] = useState<string[]>([]);
  const [subtasksToRemove, setSubtasksToRemove] = useState<string[]>([]);
  const { canAccess, role } = useFeatureAccess();

  const canShowBudgetTab = canAccess('BUDGET_TAB');

  // Track unsaved changes for update tasks
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialValues, setInitialValues] = useState<any>(null);

  // Internal task state that can be updated when task is created
  // In subtask mode, this starts as null (creating new) and gets set after creation
  const [currentTask, setCurrentTask] = useState<Task | null>(taskToEdit || null);

  // DateConflictModal state
  const [showDateConflictModal, setShowDateConflictModal] = useState(false);
  const [dateConflicts, setDateConflicts] = useState<any[]>([]);
  const [proposedMainTaskStartDate, setProposedMainTaskStartDate] =
    useState('');
  const [proposedMainTaskEndDate, setProposedMainTaskEndDate] = useState('');
  const [pendingSubmissionShouldClose, setPendingSubmissionShouldClose] =
    useState(true);

  // Budget request validation error
  const [budgetRequestError, setBudgetRequestError] = useState<string | null>(
    null,
  );

  // Discard changes confirmation modal state
  const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(false);

  // Import template modal state
  const [showImportTemplateModal, setShowImportTemplateModal] = useState(false);

  // Delegated task modal state
  const [showDelegatedTaskModal, setShowDelegatedTaskModal] = useState(false);
  const [delegatedTaskData, setDelegatedTaskData] =
    useState<DelegatedTaskData | null>(null);
  const [pendingDelegatedSubmitData, setPendingDelegatedSubmitData] = useState<{
    values: CreateEditTaskValuesProps;
    shouldClose: boolean;
  } | null>(null);

  // Precedent conflict modal state
  const [showPrecedentConflictModal, setShowPrecedentConflictModal] =
    useState(false);
  const [precedentConflicts, setPrecedentConflicts] = useState<
    PrecedentConflict[]
  >([]);
  const [pendingPrecedentSubmitData, setPendingPrecedentSubmitData] = useState<{
    values: CreateEditTaskValuesProps;
    shouldClose: boolean;
  } | null>(null);

  // Subtask out of range modal state
  const [showSubtaskOutOfRangeModal, setShowSubtaskOutOfRangeModal] =
    useState(false);
  const [subtaskOutOfRangeData, setSubtaskOutOfRangeData] =
    useState<SubtaskOutOfRangeData | null>(null);
  const [pendingSubtaskOutOfRangeSubmitData, setPendingSubtaskOutOfRangeSubmitData] =
    useState<{
      values: CreateEditTaskValuesProps;
      shouldClose: boolean;
    } | null>(null);

  // Task creation and update hooks
  const { createTaskWithData, loading: createLoading } = useCreateTask();
  const { createSubtaskWithData, loading: createSubtaskLoading } = useCreateSubtask();
  const { updateTask, loading: updateLoading } = useUpdateTask();

  // Budget request state and hooks
  const { fetchBudgetRequestsByTaskId } = useBudget();
  const [existingBudgetRequests, setExistingBudgetRequests] = useState<
    BudgetRequest[]
  >([]);
  // Fetch existing budget requests when task changes
  useEffect(() => {
    if (currentTask?.taskId && canShowBudgetTab) {
      fetchBudgetRequestsByTaskId(currentTask.taskId)
        .then((budgetRequests) => {
          setExistingBudgetRequests(budgetRequests || []);
        })
        .catch(() => {
          setExistingBudgetRequests([]);
        });
    } else {
      setExistingBudgetRequests([]);
    }
  }, [currentTask?.taskId, fetchBudgetRequestsByTaskId, canShowBudgetTab]);

  // Check for active budget requests
  const hasActiveBudgetRequest = existingBudgetRequests.some(
    (request) => request.status === BudgetRequestStatus.ACTIVE,
  );

  const isSubmitting = createLoading || createSubtaskLoading || updateLoading;

  // Update currentTask when task prop changes or modal opens
  // In subtask mode, always reset to null (creating new subtask)
  // In task mode, use the task to edit
  useEffect(() => {
    if (isOpen) {
      setCurrentTask(taskToEdit || null);
    }
  }, [taskToEdit, isOpen]);

  // Set initial tab when modal opens with initialTab prop
  useEffect(() => {
    if (isOpen && initialTab) {
      setTab(initialTab);
    }
  }, [isOpen, initialTab]);

  // Calculate existing subtasks (same logic as in GeneralTab)
  const getExistingSubtasks = () => {
    if (!currentTask?.taskId) return [];
    const currentSubtasks = tasks.filter(
      (t) => t.parentTaskId === currentTask.taskId,
    );

    // Add tasks that are pending to be added as subtasks
    const tasksToAdd = tasks.filter((t) =>
      subtasksToAdd.includes(t.taskId || ''),
    );

    // Remove tasks that are pending to be removed
    const allSubtasks = [...currentSubtasks, ...tasksToAdd];
    const finalSubtasks = allSubtasks.filter(
      (t) => !subtasksToRemove.includes(t.taskId || ''),
    );

    return finalSubtasks;
  };

  const resetForm = useCallback(() => {
    // Reset all form states
    setSelectedCrews([]);
    setSelectedOrganization(null);
    setDelegationType('crew');
    setDocuments([]);
    setDeleteDocuments([]);
    setUploadedDocuments([]);
    setPreseding([]);
    setPresedingEvent([]);
    setSubtasksToAdd([]);
    setSubtasksToRemove([]);
    setTab(initialTab || '0');
    // Reset change tracking states
    setHasUnsavedChanges(false);
    setInitialValues(null);
    // Reset DateConflictModal states
    setShowDateConflictModal(false);
    setDateConflicts([]);
    setProposedMainTaskStartDate('');
    setProposedMainTaskEndDate('');
    setPendingSubmissionShouldClose(true);
    // Reset budget request error
    setBudgetRequestError(null);
    // Reset Precedent Conflict Modal state
    setShowPrecedentConflictModal(false);
    setPrecedentConflicts([]);
    setPendingPrecedentSubmitData(null);
    // Reset Subtask Out of Range Modal state
    setShowSubtaskOutOfRangeModal(false);
    setSubtaskOutOfRangeData(null);
    setPendingSubtaskOutOfRangeSubmitData(null);
    // Reset current task - use taskToEdit (null in subtask mode) not task (parent in subtask mode)
    setCurrentTask(taskToEdit || null);
  }, [taskToEdit, initialTab]);

  // Date conflict detection function
  const detectDateConflicts = (
    mainTaskStartDate: string,
    mainTaskEndDate: string,
  ) => {
    const conflicts: any[] = [];

    // Get current subtasks (existing + new ones being added)
    const allSubtaskIds = [
      ...(currentTask?.children
        ?.map((s: Task) => s.taskId)
        .filter(
          (id: string | undefined) => id && !subtasksToRemove.includes(id),
        ) || []),
      ...subtasksToAdd,
    ];

    // Check for conflicts with all relevant subtasks
    allSubtaskIds.forEach((subtaskId) => {
      const subtask = tasks.find((t) => t.taskId === subtaskId);
      if (subtask && subtask.startTime && subtask.endTime) {
        const subtaskStartDate =
          typeof subtask.startTime === 'string'
            ? subtask.startTime
            : new Date(subtask.startTime).toISOString().split('T')[0];
        const subtaskEndDate =
          typeof subtask.endTime === 'string'
            ? subtask.endTime
            : new Date(subtask.endTime).toISOString().split('T')[0];

        // Check if subtask starts before main task
        if (subtaskStartDate < mainTaskStartDate) {
          conflicts.push({
            subtaskName: subtask.name,
            subtaskId: subtask.taskId,
            conflictType: 'startDate',
            subtaskDate: subtaskStartDate,
            mainTaskDate: mainTaskStartDate,
          });
        }

        // Check if subtask ends after main task
        if (subtaskEndDate > mainTaskEndDate) {
          conflicts.push({
            subtaskName: subtask.name,
            subtaskId: subtask.taskId,
            conflictType: 'endDate',
            subtaskDate: subtaskEndDate,
            mainTaskDate: mainTaskEndDate,
          });
        }
      }
    });

    return conflicts;
  };

  // Initialize documents from task data
  useEffect(() => {
    if (currentTask) {
      // Edit mode - initialize with task data
      if (currentTask.documents) {
        const documentArray = currentTask.documents.map((document) => ({
          documentUrl: document.url,
          name: document.name,
          documentId: document.documentId,
          delegateOrganizationId: document.delegateOrganizationId,
        }));
        setDocuments(
          documentArray.filter((doc) => doc.delegateOrganizationId == null),
        );
      }
    } else {
      // Create mode - reset the form
      resetForm();
    }
  }, [currentTask, resetForm]);

  // Initialize dependencies from task data
  useEffect(() => {
    if (currentTask) {
      setPreseding(currentTask.presedentTaskId || []);
      setPresedingEvent(currentTask.presedentEventId || []);
    } else {
      setPreseding([]);
      setPresedingEvent([]);
    }
  }, [currentTask]);

  // Walk down the children tree. A task cannot depend on its own subtask
  // (subtask dates are contained within the parent's range).
  const getDescendantTaskIds = (
    taskId: string | undefined,
    allTasks: Task[],
  ): Set<string> => {
    const descendants = new Set<string>();
    if (!taskId) return descendants;
    const stack: string[] = [taskId];
    while (stack.length) {
      const id = stack.pop()!;
      for (const t of allTasks) {
        if (t.parentTaskId === id && t.taskId && !descendants.has(t.taskId)) {
          descendants.add(t.taskId);
          stack.push(t.taskId);
        }
      }
    }
    return descendants;
  };

  // Get dependent task IDs to prevent circular dependencies
  const getDependentTaskIds = (
    task: Task,
    tasks: Task[],
    visited: Set<string> = new Set(),
  ): string[] => {
    const taskId = task.taskId;

    // Handle edge cases
    if (!taskId) {
      return [];
    }

    // Prevent infinite recursion by tracking visited tasks
    if (visited.has(taskId)) {
      return [];
    }

    visited.add(taskId);
    const precedentTaskIds = task.presedentTaskId || [];

    const dependentTaskIds = precedentTaskIds
      .filter(
        (precedentTaskId) => precedentTaskId && precedentTaskId !== taskId,
      ) // Filter out self-references
      .map((precedentTaskId) => {
        const precedentTask = tasks.find((t) => t.taskId === precedentTaskId);
        return precedentTask
          ? getDependentTaskIds(precedentTask, tasks, new Set(visited))
          : [];
      })
      .flat();

    return [...precedentTaskIds, ...dependentTaskIds];
  };

  // Pre-compute ancestor and descendant sets of the current task. Both branches
  // of the family tree are invalid as preceding tasks because parent/child date
  // ranges are nested by definition.
  const currentTaskAncestors = getAncestorTaskIds(currentTask, tasks);
  const currentTaskDescendants = getDescendantTaskIds(
    currentTask?.taskId,
    tasks,
  );

  // Filter tasks to prevent circular dependencies
  const tasksWithDependencies = tasks
    .map((taskItem) => {
      try {
        return {
          ...taskItem,
          dependencies: getDependentTaskIds(taskItem, tasks),
        };
      } catch (error) {
        console.warn(
          'Error calculating dependencies for task:',
          taskItem.taskId,
          error,
        );
        return {
          ...taskItem,
          dependencies: [],
        };
      }
    })
    .filter((taskItem) => {
      const currentTaskId = currentTask?.taskId || '';
      if (!currentTaskId) return true;
      if (taskItem.taskId === currentTaskId) return false;
      if (taskItem.taskId && currentTaskAncestors.has(taskItem.taskId)) {
        return false;
      }
      if (taskItem.taskId && currentTaskDescendants.has(taskItem.taskId)) {
        return false;
      }
      // Cycle-creators stay visible: DependenciesTab disables them with a
      // "would create cycle" hint via cycleCreatingTaskIds below.
      return true;
    });

  const cycleCreatingTaskIds = new Set<string>(
    tasksWithDependencies
      .filter(
        (t) =>
          !!t.taskId &&
          !!currentTask?.taskId &&
          t.dependencies.includes(currentTask.taskId),
      )
      .map((t) => t.taskId!),
  );
  // Initial values
  // Date-only values (YYYY-MM-DD, Date, or ISO) → the YYYY-MM-DD an
  // <input type="date"> needs, without the UTC shift of toISOString().
  const toDateInputValue = (value: string | Date | null | undefined): string =>
    formatTaskDateForBackend(parseTaskDate(value)) ?? '';

  // A new task starts on its parent's range (subtask mode), else the
  // project's, so the picker never falls back to "today" for a project
  // that hasn't started yet.
  const defaultStartTime = toDateInputValue(
    parentTask?.startTime || project?.startDate,
  );
  const defaultEndTime = toDateInputValue(
    parentTask?.endTime || project?.endDate,
  );

  const getInitialValues = useCallback((): CreateEditTaskValuesProps => ({
    name: currentTask?.name || '',
    description: currentTask?.description || '',
    taskType: currentTask?.taskType || '',
    status: currentTask?.status || TaskStatus.ACTIVE,
    otherType: currentTask?.otherType || null,
    startTime: currentTask?.startTime
      ? toDateInputValue(currentTask.startTime)
      : defaultStartTime,
    endTime: currentTask?.endTime
      ? toDateInputValue(currentTask.endTime)
      : defaultEndTime,
    budget: currentTask?.budget || null,
    parentTaskId: currentTask?.parentTaskId || null,
    crewIds:
      currentTask?.crews
        ?.map((crew) => crew.crewId)
        .filter((id): id is string => id !== undefined) || [],
    delegateOrganizationId: currentTask?.delegateOrganizationId || null,
    delegationType: currentTask?.delegateOrganizationId
      ? 'subcontractor'
      : 'crew',
    presedentTaskId: currentTask?.presedentTaskId || [],
    presedentEventId: currentTask?.presedentEventId || [],
    documents: [],
    deleteDocuments: currentTask?.deleteDocuments || [],
    superintendentId: currentTask?.superintendentId || null,
    note: currentTask?.note || null,
    manHours: currentTask?.manHours || null,
    timeConstraint: currentTask?.timeConstraint || null,
    typeOfTimeConstraint: currentTask?.typeOfTimeConstraint || null,
    equipments: [],
    materials: [],
  }), [currentTask, defaultStartTime, defaultEndTime]);

  // Separate function for refreshing data without closing modal
  const refreshTaskData = () => {
    successTask(); // This only refreshes the task list
  };

  // Check if task dates have changed
  const haveDatesChanged = (
    newStartTime: string,
    newEndTime: string,
    originalTask: Task | null,
  ): boolean => {
    if (!originalTask?.startTime || !originalTask?.endTime) {
      return false;
    }

    // Helper function to normalize date to YYYY-MM-DD format
    const normalizeDate = (date: string | Date): string => {
      if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return date;
      }
      return new Date(date).toISOString().split('T')[0];
    };

    const originalStartDate = normalizeDate(originalTask.startTime);
    const originalEndDate = normalizeDate(originalTask.endTime);
    const normalizedNewStartTime = normalizeDate(newStartTime);
    const normalizedNewEndTime = normalizeDate(newEndTime);

    return (
      normalizedNewStartTime !== originalStartDate ||
      normalizedNewEndTime !== originalEndDate
    );
  };

  // Check if task is delegated and return data for modal
  // This checks: 1) task itself is delegated, 2) task has delegated subtasks, 3) task is a subtask of a delegated parent
  const checkDelegatedTask = (
    task: Task | null,
    allTasks: Task[],
  ): DelegatedTaskData | null => {
    if (!task?.taskId) {
      return null;
    }

    // Case 1: Task itself is delegated
    if (task.delegateOrganizationId || task.delegateTaskId) {
      return {
        taskId: task.taskId,
        taskName: task.name,
        delegateOrganizationName: task.delegateOrganization?.name,
      };
    }

    // Case 2: Task has delegated subtasks (children)
    const delegatedChildren = task.children?.filter(
      (child) => child.delegateOrganizationId || child.delegateTaskId,
    );
    if (delegatedChildren && delegatedChildren.length > 0) {
      const firstDelegatedChild = delegatedChildren[0];
      return {
        taskId: task.taskId,
        taskName: task.name,
        delegateOrganizationName:
          firstDelegatedChild.delegateOrganization?.name ||
          `${delegatedChildren.length} delegated subtask(s)`,
      };
    }

    // Case 3: Task is a subtask of a delegated parent
    if (task.parentTaskId) {
      const parentTask = allTasks.find((t) => t.taskId === task.parentTaskId);
      console.log('=== Case 3 Debug ===');
      console.log('Looking for parent with ID:', task.parentTaskId);
      console.log('Found parentTask:', parentTask);
      console.log('parentTask.delegateOrganizationId:', parentTask?.delegateOrganizationId);
      console.log('parentTask.delegateTaskId:', parentTask?.delegateTaskId);

      if (
        parentTask &&
        (parentTask.delegateOrganizationId || parentTask.delegateTaskId)
      ) {
        return {
          taskId: task.taskId,
          taskName: task.name,
          delegateOrganizationName: parentTask.delegateOrganization?.name,
        };
      }
    }

    return null;
  };

  /**
   * Conflicts for what the form currently holds — the precedents the user just
   * picked, not the ones saved on the record. Reading the saved list meant
   * adding a late precedent without touching the dates slipped through
   * unnoticed, which is the whole reason this check exists.
   */
  const checkPrecedentConflicts = (
    newStartDate: string,
    tasksList: Task[],
    eventsList: Event[],
    precedentTaskIds?: string[] | null,
    precedentEventIds?: string[] | null,
  ): PrecedentConflict[] =>
    findPrecedentConflicts({
      startTime: newStartDate,
      precedentTaskIds: precedentTaskIds ?? currentTask?.presedentTaskId,
      precedentEventIds: precedentEventIds ?? currentTask?.presedentEventId,
      tasks: tasksList,
      events: eventsList,
    });

  // Check if subtask is being edited outside parent's date range
  const checkSubtaskOutOfParentRange = (
    newStartDate: string,
    newEndDate: string,
    tasksList: Task[],
  ): SubtaskOutOfRangeData | null => {
    if (!currentTask?.parentTaskId) return null; // Not a subtask

    const parentTask = tasksList.find((t) => t.taskId === currentTask.parentTaskId);

    if (!parentTask || !parentTask.startTime || !parentTask.endTime) {
      return null;
    }

    const parentStartDate =
      typeof parentTask.startTime === 'string'
        ? parentTask.startTime
        : new Date(parentTask.startTime).toISOString().split('T')[0];

    const parentEndDate =
      typeof parentTask.endTime === 'string'
        ? parentTask.endTime
        : new Date(parentTask.endTime).toISOString().split('T')[0];

    // Determine conflict type
    const beforeStart = newStartDate < parentStartDate;
    const afterEnd = newEndDate > parentEndDate;

    if (!beforeStart && !afterEnd) {
      return null; // No conflict
    }

    let conflictType: 'before_start' | 'after_end' | 'both';
    if (beforeStart && afterEnd) {
      conflictType = 'both';
    } else if (beforeStart) {
      conflictType = 'before_start';
    } else {
      conflictType = 'after_end';
    }

    return {
      subtaskId: currentTask.taskId!,
      subtaskName: currentTask.name,
      parentTaskId: parentTask.taskId!,
      parentTaskName: parentTask.name,
      parentStartDate,
      parentEndDate,
      subtaskNewStartDate: newStartDate,
      subtaskNewEndDate: newEndDate,
      conflictType,
    };
  };

  const handleSubmitInternal = async (
    values: CreateEditTaskValuesProps,
    shouldClose: boolean = true,
    options: {
      skipDelegatedCheck?: boolean;
      skipPrecedentCheck?: boolean;
      skipSubtaskOutOfRangeCheck?: boolean;
    } = {},
  ) => {
    const {
      skipDelegatedCheck = false,
      skipPrecedentCheck = false,
      skipSubtaskOutOfRangeCheck = false,
    } = options;
    try {
      const formattedValues = {
        ...values,
        // Format dates for Pattern B (date-only fields) - send as YYYY-MM-DD strings
        startTime:
          formatDateForRequest(values.startTime, true) || values.startTime,
        endTime: formatDateForRequest(values.endTime, true) || values.endTime,
        // Use Formik's crewIds (values.crewIds) as the source of truth, not the
        // separate `selectedCrews` display state — `selectedCrews` is only
        // hydrated when the Assignment tab has actually been mounted, so a save
        // from the General tab alone would otherwise submit crewIds: [] and wipe
        // out the task's existing crew assignments (tasks_crews rows).
        crewIds: values.crewIds ?? [],
        delegateOrganizationId:
          delegationType === 'subcontractor' && selectedOrganization
            ? selectedOrganization.organizationId
            : null,
        deleteDocuments,
        // Use the local state for dependencies instead of form values
        presedentTaskId: preseding,
        presedentEventId: presedingEvent,
        // Add subtasks data if there are changes
        subtasks:
          subtasksToAdd.length > 0 || subtasksToRemove.length > 0
            ? {
                add: subtasksToAdd,
                remove: subtasksToRemove,
              }
            : undefined,
      };

      // Check if delegated task dates are being changed (only in edit mode)
      if (!skipDelegatedCheck && currentTask?.taskId) {
        // Find the task in the tasks array to ensure we have the most up-to-date delegation data
        // This is consistent with how TimeLine checks delegation
        const taskFromList = tasks.find((t) => t.taskId === currentTask.taskId);
        const taskToCheck = taskFromList || currentTask;

        const datesChanged = haveDatesChanged(
          values.startTime,
          values.endTime,
          currentTask,
        );

        // DEBUG: Log delegation check info
        console.log('=== Delegation Check Debug ===');
        console.log('currentTask:', currentTask);
        console.log('taskFromList:', taskFromList);
        console.log('taskToCheck.delegateOrganizationId:', taskToCheck?.delegateOrganizationId);
        console.log('taskToCheck.delegateTaskId:', taskToCheck?.delegateTaskId);
        console.log('datesChanged:', datesChanged);
        console.log('tasks array length:', tasks.length);

        if (datesChanged) {
          const delegatedData = checkDelegatedTask(taskToCheck, tasks);
          console.log('delegatedData result:', delegatedData);

          if (delegatedData) {
            setDelegatedTaskData(delegatedData);
            setPendingDelegatedSubmitData({
              values,
              shouldClose,
            });
            setShowDelegatedTaskModal(true);
            return;
          }
        }
      }

      // Precedent conflicts. Not gated on the dates having changed and not
      // limited to edit mode: picking a precedent that ends after this task is
      // just as wrong on a brand-new task, and just as wrong when the dates
      // were left alone.
      if (!skipPrecedentCheck) {
        const precedentConflictsList = checkPrecedentConflicts(
          values.startTime,
          tasks,
          events,
          values.presedentTaskId,
          values.presedentEventId,
        );

        if (precedentConflictsList.length > 0) {
          setPrecedentConflicts(precedentConflictsList);
          setPendingPrecedentSubmitData({
            values,
            shouldClose,
          });
          setShowPrecedentConflictModal(true);
          return;
        }
      }

      // Check if this task is a subtask being edited outside parent's range
      if (!skipSubtaskOutOfRangeCheck && currentTask?.taskId && currentTask.parentTaskId) {
        const datesChanged = haveDatesChanged(
          values.startTime,
          values.endTime,
          currentTask,
        );

        if (datesChanged) {
          const outOfRangeData = checkSubtaskOutOfParentRange(
            values.startTime,
            values.endTime,
            tasks,
          );

          if (outOfRangeData) {
            setSubtaskOutOfRangeData(outOfRangeData);
            setPendingSubtaskOutOfRangeSubmitData({
              values,
              shouldClose,
            });
            setShowSubtaskOutOfRangeModal(true);
            return;
          }
        }
      }

      // Check for date conflicts if there are subtasks
      const hasSubtasks =
        (currentTask?.children && currentTask.children.length > 0) ||
        subtasksToAdd.length > 0 ||
        (currentTask?.children &&
          currentTask.children.some(
            (child) => !subtasksToRemove.includes(child.taskId || ''),
          ));

      if (hasSubtasks && formattedValues.startTime && formattedValues.endTime) {
        // Ensure dates are strings
        const startDateStr =
          typeof formattedValues.startTime === 'string'
            ? formattedValues.startTime
            : new Date(formattedValues.startTime).toISOString().split('T')[0];
        const endDateStr =
          typeof formattedValues.endTime === 'string'
            ? formattedValues.endTime
            : new Date(formattedValues.endTime).toISOString().split('T')[0];

        const conflicts = detectDateConflicts(startDateStr, endDateStr);

        if (conflicts.length > 0) {
          // Show date conflict modal and remember the original submission intention
          setDateConflicts(conflicts);
          setProposedMainTaskStartDate(startDateStr);
          setProposedMainTaskEndDate(endDateStr);
          setPendingSubmissionShouldClose(shouldClose);
          setShowDateConflictModal(true);
          return; // Stop submission until user confirms
        }
      }

      let updatedTask: Task | null = null;

      if (currentTask?.taskId) {
        // Update existing task (inventory is now managed via warehouse hooks directly)
        updatedTask = await updateTask({
          taskId: currentTask.taskId,
          name: formattedValues.name,
          description: formattedValues.description,
          taskType: formattedValues.taskType,
          otherType: formattedValues.otherType,
          budget: formattedValues.budget,
          manHours: formattedValues.manHours,
          startDate:
            formatDateForSubmission(formattedValues.startTime) ||
            formattedValues.startTime,
          endDate:
            formatDateForSubmission(formattedValues.endTime) ||
            formattedValues.endTime,
          timeConstraint: formattedValues.timeConstraint,
          selectedTypeConstraint: formattedValues.typeOfTimeConstraint,
          crewIds: formattedValues.crewIds,
          presedentTaskId: formattedValues.presedentTaskId,
          presedentEventId: formattedValues.presedentEventId,
          deleteDocuments: formattedValues.deleteDocuments,
          documents: uploadedDocuments,
          parentTaskId: formattedValues.parentTaskId,
          delegateOrganizationId: formattedValues.delegateOrganizationId,
          rescheduledTask: [], // Not used in current form
          note: formattedValues.note,
          subtasks: formattedValues.subtasks,
          superintendentId: formattedValues.superintendentId,
          projectId: currentTask.projectId || null,
          initialData: currentTask,
        });
        if (updatedTask) {
          // Update currentTask with fresh data from server (includes saved materials/equipments)
          setCurrentTask(updatedTask);

          if (shouldClose) {
            successTask();
            resetForm();
            formik.resetForm();
            onClose();
          } else {
            // For save-only: just refresh the data without resetting the form
            setUploadedDocuments([]);
            setDeleteDocuments([]);
            setSubtasksToAdd([]);
            setSubtasksToRemove([]);
            setHasUnsavedChanges(false);
            const initialState = {
              formValues: formik.values,
              uploadedDocuments: [],
              deleteDocuments: [],
              subtasksToAdd: [],
              subtasksToRemove: [],
            };
            setInitialValues(JSON.stringify(initialState));
          }
        }
      } else {
        // For new tasks/subtasks (inventory is added after task creation via warehouse hooks)

        let createdTask: Task | null = null;

        if (mode === 'subtask') {
          // In subtask mode, use createSubtaskWithData
          // parentTask is required and validated by the form's parentTaskId
          if (!parentTask?.taskId) {
            console.error('Cannot create subtask: no parent task ID provided');
            return;
          }
          createdTask = await createSubtaskWithData({
            name: formattedValues.name,
            description: formattedValues.description,
            taskType: formattedValues.taskType,
            status: formattedValues.status,
            otherType: formattedValues.otherType,
            budget: formattedValues.budget,
            manHours: formattedValues.manHours,
            startDate: formattedValues.startTime,
            endDate: formattedValues.endTime,
            timeConstraint: formattedValues.timeConstraint,
            selectedTypeConstraint: formattedValues.typeOfTimeConstraint,
            crewIds: formattedValues.crewIds,
            presedentTaskId: formattedValues.presedentTaskId,
            presedentEventId: formattedValues.presedentEventId,
            deleteDocuments: formattedValues.deleteDocuments,
            documents: uploadedDocuments,
            delegateOrganizationId: formattedValues.delegateOrganizationId,
            note: formattedValues.note,
            superintendentId: formattedValues.superintendentId,
            projectId: project?.projectId || parentTask?.projectId || projectId || '',
            parentTaskId: parentTask.taskId,
          });
        } else {
          // In task mode, use createTaskWithData
          createdTask = await createTaskWithData({
            name: formattedValues.name,
            description: formattedValues.description,
            taskType: formattedValues.taskType,
            status: formattedValues.status,
            otherType: formattedValues.otherType,
            budget: formattedValues.budget,
            manHours: formattedValues.manHours,
            startDate: formatDateForSubmission(formattedValues.startTime),
            endDate: formatDateForSubmission(formattedValues.endTime),
            timeConstraint: formattedValues.timeConstraint,
            selectedTypeConstraint: formattedValues.typeOfTimeConstraint,
            crewIds: formattedValues.crewIds,
            presedentTaskId: formattedValues.presedentTaskId,
            presedentEventId: formattedValues.presedentEventId,
            deleteDocuments: formattedValues.deleteDocuments,
            documents: uploadedDocuments,
            parentTaskId: formattedValues.parentTaskId,
            delegateOrganizationId: formattedValues.delegateOrganizationId,
            rescheduledTask: [], // Not used in current form
            note: formattedValues.note,
            superintendentId: formattedValues.superintendentId,
            projectId: project?.projectId || task?.projectId || projectId || '',
            subtasks: formattedValues.subtasks,
          });
        }
        if (createdTask) {
          if (shouldClose) {
            successTask();
            resetForm();
            formik.resetForm();
            onClose();
          } else {
            // For create-only: update the currentTask to switch to edit mode
            setCurrentTask(createdTask);

            // Reset change tracking, but keep form values
            setUploadedDocuments([]);
            setDeleteDocuments([]);
            setSubtasksToAdd([]);
            setSubtasksToRemove([]);
            setHasUnsavedChanges(false);
            const initialState = {
              formValues: formik.values,
              uploadedDocuments: [],
              deleteDocuments: [],
              subtasksToAdd: [],
              subtasksToRemove: [],
            };
            setInitialValues(JSON.stringify(initialState));
          }
        } else {
          // createdTask is null/undefined - API call may have failed
        }
      }
    } catch (error) {
      console.error('Error in handleSubmitInternal:', error);
    }
  };

  // Submit handlers for different actions
  const handleSubmit = async (values: CreateEditTaskValuesProps) => {
    await handleSubmitInternal(values, true); // Save & Close
  };

  /**
   * Surfaces validation errors instead of failing silently: marks the offending
   * fields as touched, switches to the first tab that holds an error, and shows
   * a toast summarising what needs fixing.
   */
  const showValidationErrors = (errors: Record<string, any>) => {
    const errorFields = Object.keys(errors);

    const touchedFields: Record<string, boolean> = {};
    errorFields.forEach((field) => {
      touchedFields[field] = true;
    });
    formik.setTouched({ ...formik.touched, ...touchedFields });

    const firstErrorTab = TAB_ORDER.find((tabId) =>
      errorFields.some((field) => FIELD_TAB_MAP[field] === tabId),
    );
    if (firstErrorTab && firstErrorTab !== tab) {
      setTab(firstErrorTab);
    }

    // Array/object fields (materials, equipments, documents) yield nested error
    // shapes rather than a plain string — fall back to the tab name for those.
    const messages = errorFields
      .map((field) => errors[field])
      .filter((message): message is string => typeof message === 'string');

    const location =
      firstErrorTab && TAB_LABELS[firstErrorTab]
        ? ` in ${TAB_LABELS[firstErrorTab]}`
        : '';

    if (messages.length === 0) {
      toast.error(`Fix the highlighted fields before saving${location}.`);
      return;
    }

    toast.error(
      messages.length === 1
        ? messages[0]
        : `Fix ${messages.length} fields before saving${location}: ${messages.join(' · ')}`,
    );
  };

  const handleSaveAndClose = async () => {
    const errors = await formik.validateForm();

    // Check if user selected "Create Budget Request" but hasn't created one
    if (
      currentTask?.taskId &&
      canShowBudgetTab &&
      delegationType === 'subcontractor' &&
      !hasActiveBudgetRequest
    ) {
      setBudgetRequestError(
        'You need to create a bid request before saving. Click "Open Bid Request Form" in the Assignment tab.',
      );
      setTab('1'); // Switch to Assignment tab
      return;
    }

    setBudgetRequestError(null); // Clear any previous errors

    if (Object.keys(errors).length === 0) {
      await handleSubmitInternal(formik.values, true);
    } else {
      showValidationErrors(errors);
    }
  };

  const handleSaveOnly = async () => {
    const errors = await formik.validateForm();

    // Check if user selected "Create Budget Request" but hasn't created one
    if (
      currentTask?.taskId &&
      canShowBudgetTab &&
      delegationType === 'subcontractor' &&
      !hasActiveBudgetRequest
    ) {
      setBudgetRequestError(
        'You need to create a bid request before saving. Click "Open Bid Request Form" in the Assignment tab.',
      );
      setTab('1'); // Switch to Assignment tab
      return;
    }

    setBudgetRequestError(null); // Clear any previous errors

    if (Object.keys(errors).length === 0) {
      await handleSubmitInternal(formik.values, false);
    } else {
      showValidationErrors(errors);
    }
  };

  // Delegated task modal handlers
  const handleDelegatedTaskCancel = () => {
    setShowDelegatedTaskModal(false);
    setDelegatedTaskData(null);
    setPendingDelegatedSubmitData(null);
  };

  const handleDelegatedTaskConfirm = async () => {
    if (!pendingDelegatedSubmitData) return;

    setShowDelegatedTaskModal(false);
    const { values, shouldClose } = pendingDelegatedSubmitData;
    setPendingDelegatedSubmitData(null);
    setDelegatedTaskData(null);

    // Continue with submission, skipping the delegated check
    await handleSubmitInternal(values, shouldClose, { skipDelegatedCheck: true });
  };

  // Precedent conflict modal handlers
  const handlePrecedentConflictCancel = () => {
    setShowPrecedentConflictModal(false);
    setPrecedentConflicts([]);
    setPendingPrecedentSubmitData(null);
  };

  const handlePrecedentConflictConfirm = async () => {
    if (!pendingPrecedentSubmitData) return;

    setShowPrecedentConflictModal(false);
    const { values, shouldClose } = pendingPrecedentSubmitData;
    setPendingPrecedentSubmitData(null);
    setPrecedentConflicts([]);

    // Continue with submission, skipping the precedent check
    await handleSubmitInternal(values, shouldClose, { skipPrecedentCheck: true });
  };

  // Subtask out of range modal handlers
  const handleSubtaskOutOfRangeCancel = () => {
    setShowSubtaskOutOfRangeModal(false);
    setSubtaskOutOfRangeData(null);
    setPendingSubtaskOutOfRangeSubmitData(null);
  };

  const handleExtendParent = async () => {
    if (!pendingSubtaskOutOfRangeSubmitData || !subtaskOutOfRangeData) return;

    setShowSubtaskOutOfRangeModal(false);
    const { values, shouldClose } = pendingSubtaskOutOfRangeSubmitData;

    // Calculate new parent dates
    let newParentStartDate = subtaskOutOfRangeData.parentStartDate;
    let newParentEndDate = subtaskOutOfRangeData.parentEndDate;

    if (
      subtaskOutOfRangeData.conflictType === 'before_start' ||
      subtaskOutOfRangeData.conflictType === 'both'
    ) {
      newParentStartDate = subtaskOutOfRangeData.subtaskNewStartDate;
    }
    if (
      subtaskOutOfRangeData.conflictType === 'after_end' ||
      subtaskOutOfRangeData.conflictType === 'both'
    ) {
      newParentEndDate = subtaskOutOfRangeData.subtaskNewEndDate;
    }

    // First update the parent task dates using direct GraphQL call
    try {
      await graphQlClient.request(TasksGraphQL.mutations.updateTask, {
        task: {
          taskId: subtaskOutOfRangeData.parentTaskId,
          startTime: newParentStartDate,
          endTime: newParentEndDate,
        },
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    } catch (error) {
      console.error('Error updating parent task dates:', error);
      setPendingSubtaskOutOfRangeSubmitData(null);
      setSubtaskOutOfRangeData(null);
      return;
    }

    setPendingSubtaskOutOfRangeSubmitData(null);
    setSubtaskOutOfRangeData(null);

    // Continue with the subtask submission, skipping the out of range check
    await handleSubmitInternal(values, shouldClose, { skipSubtaskOutOfRangeCheck: true });
  };

  const handleLeaveSubtaskOutside = async () => {
    if (!pendingSubtaskOutOfRangeSubmitData) return;

    setShowSubtaskOutOfRangeModal(false);
    const { values, shouldClose } = pendingSubtaskOutOfRangeSubmitData;
    setPendingSubtaskOutOfRangeSubmitData(null);
    setSubtaskOutOfRangeData(null);

    // Continue with submission, skipping the out of range check
    await handleSubmitInternal(values, shouldClose, { skipSubtaskOutOfRangeCheck: true });
  };

  // Initialize formik
  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: validationTaskSchema,
    enableReinitialize: false, // Disable to prevent form resets
    validateOnChange: true, // Validate as user types
    validateOnBlur: true, // Validate when user leaves field
    onSubmit: handleSubmit,
  });

  const { resetForm: formikResetForm } = formik;

  // Reinitialize form when task changes or modal opens
  useEffect(() => {
    if (isOpen) {
      const initialFormValues = getInitialValues();
      formikResetForm({ values: initialFormValues });
    }
  }, [currentTask?.taskId, isOpen, getInitialValues, formikResetForm]);

  // Track initial values for change detection (only for existing tasks)
  useEffect(() => {
    if (currentTask?.taskId && isOpen) {
      const initialState = {
        formValues: getInitialValues(),
        uploadedDocuments: [],
        deleteDocuments: [],
        subtasksToAdd: [],
        subtasksToRemove: [],
      };
      setInitialValues(JSON.stringify(initialState));
      setHasUnsavedChanges(false);
    }
  }, [currentTask?.taskId, isOpen, getInitialValues]);

  // Detect changes in form values, inventory, documents, and subtasks for existing tasks
  useEffect(() => {
    if (currentTask?.taskId && initialValues) {
      const currentState = {
        formValues: formik.values,
        uploadedDocuments,
        deleteDocuments,
        subtasksToAdd,
        subtasksToRemove,
      };
      const currentValues = JSON.stringify(currentState);
      const hasChanges = currentValues !== initialValues;
      setHasUnsavedChanges(hasChanges);
    }
  }, [
    formik.values,
    uploadedDocuments,
    deleteDocuments,
    subtasksToAdd,
    subtasksToRemove,
    initialValues,
    currentTask?.taskId,
  ]);

  // Function to get tab errors
  const getTabErrors = useMemo(() => {
    const errors = formik.errors;
    const tabErrorMap: Record<string, string[]> = {
      '0': [], // General
      '1': [], // Assignment
      '2': [], // Inventory
      '3': [], // Documents
      '4': [], // Dependencies
    };

    // General tab errors
    if (errors.name) tabErrorMap['0'].push('name');
    if (errors.description) tabErrorMap['0'].push('description');
    if (errors.taskType) tabErrorMap['0'].push('taskType');
    if (errors.otherType) tabErrorMap['0'].push('otherType');
    if (errors.startTime) tabErrorMap['0'].push('startTime');
    if (errors.endTime) tabErrorMap['0'].push('endTime');
    if (errors.budget) tabErrorMap['0'].push('budget');

    // Assignment tab errors
    if (errors.delegateOrganizationId)
      tabErrorMap['1'].push('delegateOrganizationId');
    if (errors.crewIds) tabErrorMap['1'].push('crewIds');
    if (errors.manHours) tabErrorMap['1'].push('manHours');
    if (errors.timeConstraint) tabErrorMap['1'].push('timeConstraint');
    if (errors.typeOfTimeConstraint)
      tabErrorMap['1'].push('typeOfTimeConstraint');
    if (errors.superintendentId) tabErrorMap['1'].push('superintendentId');
    if (errors.note) tabErrorMap['1'].push('note');

    // Inventory tab errors - check for materials and equipments arrays
    if (errors.materials) tabErrorMap['2'].push('materials');
    if (errors.equipments) tabErrorMap['2'].push('equipments');

    // Documents tab errors - check for documents array
    if (errors.documents) tabErrorMap['3'].push('documents');

    // Dependencies tab errors - check for precedent tasks and events
    if (errors.presedentTaskId) tabErrorMap['4'].push('presedentTaskId');
    if (errors.presedentEventId) tabErrorMap['4'].push('presedentEventId');

    return tabErrorMap;
  }, [formik.errors]);

  // Function to detect changes per tab
  const getTabUnsavedChanges = useMemo(() => {
    if (!currentTask?.taskId || !initialValues) {
      return {
        '0': false, // General
        '1': false, // Assignment
        '2': false, // Inventory
        '3': false, // Documents
        '4': false, // Dependencies
      };
    }

    const initialState = JSON.parse(initialValues);
    const currentFormValues = formik.values;
    const initialFormValues = initialState.formValues;

    const tabChanges = {
      '0': false, // General
      '1': false, // Assignment
      '2': false, // Inventory
      '3': false, // Documents
      '4': false, // Dependencies
    };

    // General tab - check basic task fields
    if (
      currentFormValues.name !== initialFormValues.name ||
      currentFormValues.description !== initialFormValues.description ||
      currentFormValues.taskType !== initialFormValues.taskType ||
      currentFormValues.otherType !== initialFormValues.otherType ||
      currentFormValues.startTime !== initialFormValues.startTime ||
      currentFormValues.endTime !== initialFormValues.endTime ||
      currentFormValues.status !== initialFormValues.status ||
      currentFormValues.budget !== initialFormValues.budget ||
      JSON.stringify(subtasksToAdd) !==
        JSON.stringify(initialState.subtasksToAdd) ||
      JSON.stringify(subtasksToRemove) !==
        JSON.stringify(initialState.subtasksToRemove)
    ) {
      tabChanges['0'] = true;
    }

    // Assignment tab - check crew, organization, and assignment related fields
    if (
      JSON.stringify(currentFormValues.crewIds) !==
        JSON.stringify(initialFormValues.crewIds) ||
      currentFormValues.delegateOrganizationId !==
        initialFormValues.delegateOrganizationId ||
      currentFormValues.manHours !== initialFormValues.manHours ||
      currentFormValues.timeConstraint !== initialFormValues.timeConstraint ||
      currentFormValues.typeOfTimeConstraint !==
        initialFormValues.typeOfTimeConstraint ||
      currentFormValues.superintendentId !==
        initialFormValues.superintendentId ||
      currentFormValues.note !== initialFormValues.note
    ) {
      tabChanges['1'] = true;
    }

    // Inventory tab - changes are now managed directly via warehouse hooks (no local state)

    // Documents tab - check document changes
    if (
      JSON.stringify(uploadedDocuments) !==
        JSON.stringify(initialState.uploadedDocuments) ||
      JSON.stringify(deleteDocuments) !==
        JSON.stringify(initialState.deleteDocuments)
    ) {
      tabChanges['3'] = true;
    }

    // Dependencies tab - check precedent tasks and events
    if (
      JSON.stringify(currentFormValues.presedentTaskId) !==
        JSON.stringify(initialFormValues.presedentTaskId) ||
      JSON.stringify(currentFormValues.presedentEventId) !==
        JSON.stringify(initialFormValues.presedentEventId)
    ) {
      tabChanges['4'] = true;
    }

    return tabChanges;
  }, [
    currentTask?.taskId,
    initialValues,
    formik.values,
    uploadedDocuments,
    deleteDocuments,
    subtasksToAdd,
    subtasksToRemove,
  ]);

  /**
   * Conflicts for what the form holds right now, recomputed as the user picks
   * precedents or moves the dates. Feeds the Dependencies tab badge and the
   * inline notice inside it — the save-time check runs the same function.
   */
  const livePrecedentConflicts = useMemo(
    () =>
      findPrecedentConflicts({
        startTime: formik.values.startTime,
        precedentTaskIds: formik.values.presedentTaskId,
        precedentEventIds: formik.values.presedentEventId,
        tasks,
        events,
      }),
    [
      formik.values.startTime,
      formik.values.presedentTaskId,
      formik.values.presedentEventId,
      tasks,
      events,
    ],
  );

  const precedentWarningText = describePrecedentConflicts(
    livePrecedentConflicts,
  );

  // Function to get warning icon and styling for tabs
  const getTabWarning = (tabId: string) => {
    const hasErrors = getTabErrors[tabId].length > 0;
    const hasUnsaved =
      currentTask?.taskId &&
      getTabUnsavedChanges[tabId as keyof typeof getTabUnsavedChanges];

    // A late precedent is a warning, not a validation error: it never blocks
    // saving, so it stays amber and never claims the red slot.
    if (tabId === '4' && livePrecedentConflicts.length > 0 && !hasErrors) {
      return {
        leftIcon: (
          <Warning
            className="text-amber-500"
            style={{ fontSize: '16px' }}
            titleAccess={precedentWarningText}
          />
        ),
        hasError: false,
        hasUnsavedChanges: true,
        className: 'text-amber-500',
      };
    }

    if (hasErrors) {
      // Priority: errors get red warning icon
      return {
        leftIcon: (
          <Warning className="text-red-500" style={{ fontSize: '16px' }} />
        ),
        hasError: true,
        className: 'text-red-500',
      };
    } else if (hasUnsaved) {
      // Secondary: unsaved changes get yellow/amber warning icon
      return {
        leftIcon: (
          <Warning className="text-amber-500" style={{ fontSize: '16px' }} />
        ),
        hasError: false,
        hasUnsavedChanges: true,
        className: 'text-amber-500',
      };
    }

    return {
      leftIcon: undefined,
      hasError: false,
      hasUnsavedChanges: false,
      className: undefined,
    };
  };

  // Dynamic tab configuration
  const tabItems = [
    {
      id: '0',
      name: 'General',
      alwaysVisible: true,
      ...getTabWarning('0'),
    },
    {
      id: '1',
      name: 'Assignment',
      alwaysVisible: true,
      ...getTabWarning('1'),
    },
    {
      id: '2',
      name: 'Inventory',
      alwaysVisible: true,
      ...getTabWarning('2'),
    },
    {
      id: '3',
      name: 'Documents',
      alwaysVisible: true,
      ...getTabWarning('3'),
    },
    {
      id: '4',
      name: 'Dependencies',
      alwaysVisible: true,
      ...getTabWarning('4'),
    },
    {
      id: '5',
      name: 'Activity',
      alwaysVisible: false,
      condition: () => !!currentTask,
    }, // Only for existing tasks (read-only activity log)
    {
      id: '6',
      name: 'Bid',
      alwaysVisible: false,
      condition: () => !!currentTask && canShowBudgetTab, // Show for any existing task if user has access
    }, // Only for existing tasks (budget requests need existing task)
  ];

  // Filter visible tabs
  const visibleTabs = tabItems.filter(
    (item) => item.alwaysVisible || (item.condition && item.condition()),
  );

  const handleTabChange = (tabId: string) => {
    setTab(tabId);
  };

  const handleDocumentDelete = (value: any) => {
    if (value.documentId) {
      // This is an existing document from the backend
      if (!deleteDocuments?.includes(value.documentId)) {
        const newDeleteDocuments = [...deleteDocuments, value.documentId];
        setDeleteDocuments(newDeleteDocuments);
      }
    }
    // Remove from documents array (existing documents only)
    setDocuments((prev) => prev.filter((doc) => doc !== value));
  };

  const handleDownload = (pdfUrl: string) => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank'); // Open a new tab for preview
    }
  };

  const handleSubtasksChange = (add: string[], remove: string[]) => {
    setSubtasksToAdd(add);
    setSubtasksToRemove(remove);
  };

  const handleClose = () => {
    // Detect changes: in edit mode use hasUnsavedChanges, in create mode check dirty state
    const hasChanges = currentTask?.taskId
      ? hasUnsavedChanges
      : formik.dirty ||
        uploadedDocuments.length > 0;

    if (hasChanges && !isSubmitting) {
      setShowDiscardChangesModal(true);
      return;
    }

    // If we created a task but didn't refresh the list yet, refresh it now
    if (currentTask?.taskId && !task?.taskId) {
      successTask();
    }
    resetForm();
    formik.resetForm();
    onClose();
  };

  const handleDiscardConfirm = () => {
    setShowDiscardChangesModal(false);
    if (currentTask?.taskId && !task?.taskId) {
      successTask();
    }
    resetForm();
    formik.resetForm();
    onClose();
  };

  const handleDiscardCancel = () => {
    setShowDiscardChangesModal(false);
  };

  // DateConflictModal handlers
  const handleDateConflictConfirm = async () => {
    setShowDateConflictModal(false);

    // Calculate adjusted dates based on conflicts
    let adjustedStartDate = proposedMainTaskStartDate;
    let adjustedEndDate = proposedMainTaskEndDate;

    dateConflicts.forEach((conflict) => {
      if (
        conflict.conflictType === 'startDate' &&
        conflict.subtaskDate < adjustedStartDate
      ) {
        adjustedStartDate = conflict.subtaskDate;
      }
      if (
        conflict.conflictType === 'endDate' &&
        conflict.subtaskDate > adjustedEndDate
      ) {
        adjustedEndDate = conflict.subtaskDate;
      }
    });

    // Update form values with adjusted dates and resubmit
    const adjustedValues = {
      ...formik.values,
      startTime: adjustedStartDate,
      endTime: adjustedEndDate,
    };

    // Resubmit with adjusted dates, preserving the original shouldClose intention
    await handleSubmitInternal(adjustedValues, pendingSubmissionShouldClose);
  };

  const handleDateConflictCancel = () => {
    setShowDateConflictModal(false);
    setDateConflicts([]);
    setProposedMainTaskStartDate('');
    setProposedMainTaskEndDate('');
    setPendingSubmissionShouldClose(true);
  };

  // Create action buttons configuration
  const actionButtons = currentTask?.taskId
    ? [
        // For existing tasks: Show both Save and Save & Close
        {
          text: 'Save',
          onClick: handleSaveOnly,
          variant: 'secondary' as const,
          disabled: isSubmitting || !hasUnsavedChanges,
        },
        {
          text: isSubmitting ? 'Saving...' : 'Save & Close',
          onClick: handleSaveAndClose,
          variant: 'primary' as const,
          disabled: isSubmitting || !hasUnsavedChanges,
          testId: 'task-form-save-button',
        },
      ]
    : [
        // For new tasks: Show both Create and Create & Close
        {
          text: isSubmitting ? 'Creating...' : 'Create',
          onClick: handleSaveOnly,
          variant: 'secondary' as const,
          disabled: isSubmitting,
        },
        {
          text: isSubmitting ? 'Creating...' : 'Create & Close',
          onClick: handleSaveAndClose,
          variant: 'primary' as const,
          disabled: isSubmitting,
          testId: 'task-form-save-button',
        },
      ];

  if (!isOpen) return null;

  // Modal width class - subtask mode is 15px narrower
  const modalWidthClass = mode === 'subtask'
    ? 'sm:!w-[calc(75%-15px)]'
    : '';

  return (
    <SliderModal
      isOpen={isOpen}
      closeSlider={handleClose}
      actionButtons={actionButtons}
      className={`overflow-none! scrollbar-none ${modalWidthClass}`}
      isSecondaryLayer={mode === 'subtask'}
      title={
        <span className="flex flex-col gap-1">
          <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {mode === 'subtask'
              ? 'Create Subtask'
              : currentTask
                ? 'Edit Task'
                : 'Create Task'}
            {!!currentTask?.originalTaskId && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800 normal-case tracking-normal">
                Delegated
              </span>
            )}
            {!currentTask?.originalTaskId && (!!currentTask?.delegateTaskId || !!currentTask?.delegateOrganizationId) && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-800 normal-case tracking-normal">
                Delegated Out
              </span>
            )}
          </span>
          {currentTask?.name && (
            <span className="text-2xl font-bold text-gray-900 dark:text-white truncate">
              {currentTask.name}
            </span>
          )}
        </span>
      }>
      {/* Story-066: `SliderModal` has no `role="dialog"` — `task-form` is this panel's only
          stable selector. Scopes the tab bar + all tab content (name input, save button is
          scoped separately via SlideOver's own `actionButtons[].testId`, see above). */}
      <div data-testid="task-form">
      {/* Custom Tabs */}
      <Tab
        tabs={visibleTabs}
        activeTab={tab}
        onTabChange={handleTabChange}
        variant="underline"
        size="md"
        className="mb-6"
      />

      <form onSubmit={formik.handleSubmit} className="space-y-4">
        {(() => {
          switch (tab) {
            case '0':
              return (
                <GeneralTab
                  tasks={tasks}
                  task={currentTask}
                  parentTask={parentTask}
                  project={project}
                  values={formik.values}
                  errors={formik.errors}
                  touched={formik.touched}
                  setFieldValue={formik.setFieldValue}
                  setFieldTouched={formik.setFieldTouched}
                  validateField={formik.validateField}
                  handleBlur={formik.handleBlur}
                  onStatusUpdate={successTask}
                  subtasksToAdd={subtasksToAdd}
                  subtasksToRemove={subtasksToRemove}
                  onSubtasksChange={handleSubtasksChange}
                  hasActiveBudgetRequest={hasActiveBudgetRequest}
                  onCreateSubtaskClick={mode === 'task' && onCreateSubtaskClick && currentTask ? () => onCreateSubtaskClick(currentTask) : undefined}
                  onImportTemplateClick={
                    mode === 'task' && roleRank(role) >= TEMPLATE_ACTIONS_MIN_RANK
                      ? () => setShowImportTemplateModal(true)
                      : undefined
                  }
                />
              );
            case '1':
              return (
                <AssignmentTab
                  subtasks={getExistingSubtasks()}
                  values={formik.values}
                  errors={formik.errors}
                  touched={formik.touched}
                  initialData={currentTask}
                  isDelegated={!!currentTask?.originalTaskId}
                  setFieldValue={formik.setFieldValue}
                  setFieldTouched={formik.setFieldTouched}
                  validateForm={formik.validateForm}
                  handleBlur={formik.handleBlur}
                  selectedCrews={selectedCrews}
                  delegationType={delegationType}
                  projectId={currentTask?.projectId || ''}
                  taskId={currentTask?.taskId}
                  setSelectedCrews={setSelectedCrews}
                  setDelegationType={setDelegationType}
                  selectedOrganization={selectedOrganization}
                  setSelectedOrganization={setSelectedOrganization}
                  onTaskDataChange={refreshTaskData}
                  budgetRequestError={budgetRequestError}
                  onBudgetRequestErrorDismiss={() => setBudgetRequestError(null)}
                />
              );
            case '2':
              return (
                <InventoryTab
                  taskId={currentTask?.taskId}
                  isDelegated={!!currentTask?.originalTaskId}
                  taskStartDate={formik.values.startTime}
                  taskEndDate={formik.values.endTime}
                  initialSubTab={initialInventorySubTab}
                />
              );
            case '3':
              return (
                <DocumentsTab
                  avatars={uploadedDocuments}
                  documents={documents}
                  setDocuments={setDocuments}
                  handleDownload={handleDownload}
                  onFilesChange={setUploadedDocuments}
                  handleDocumentDelete={handleDocumentDelete}
                />
              );
            case '4':
              return (
                <DependenciesTab
                  tasksWithDependencies={tasksWithDependencies}
                  allTasks={tasks}
                  cycleCreatingTaskIds={cycleCreatingTaskIds}
                  events={events}
                  preseding={preseding}
                  presedingEvent={presedingEvent}
                  setPreseding={setPreseding}
                  setPresedingEvent={setPresedingEvent}
                  setFieldValue={formik.setFieldValue}
                  conflicts={livePrecedentConflicts}
                  taskStartTime={formik.values.startTime}
                />
              );
            case '5':
              return (
                <ActivityTab taskId={currentTask?.taskId} task={currentTask} />
              );
            case '6':
              return (
                <BudgetTab taskId={currentTask?.taskId} task={currentTask} />
              );
            default:
              return null;
          }
        })()}
      </form>
      </div>

      {/* DateConflictModal */}
      <DateConflictModal
        isLoading={isSubmitting}
        conflicts={dateConflicts}
        isOpen={showDateConflictModal}
        onCancel={handleDateConflictCancel}
        onConfirm={handleDateConflictConfirm}
        proposedMainTaskEndDate={proposedMainTaskEndDate}
        proposedMainTaskStartDate={proposedMainTaskStartDate}
      />

      {/* Discard Changes Confirmation Modal */}
      <ConfirmationModal
        title="Discard Changes"
        isOpen={showDiscardChangesModal}
        variant="warning"
        confirmText="Discard"
        cancelText="Keep Editing"
        onClose={handleDiscardCancel}
        onConfirm={handleDiscardConfirm}
        description="You have unsaved changes. Are you sure you want to discard them?"
      />

      {/* Import Task Template Modal - only in task mode */}
      {mode === 'task' && currentTask?.taskId && (
        <ImportTaskTemplateModal
          isOpen={showImportTemplateModal}
          onClose={() => setShowImportTemplateModal(false)}
          projectId={currentTask.projectId || project?.projectId || projectId || ''}
          projectStartDate={project?.startDate}
          projectEndDate={project?.endDate}
          parentTaskId={currentTask.taskId}
          onSuccess={() => {
            // Invalidate tasks query to refresh the list without closing the modal
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
          }}
        />
      )}

      {/* Delegated Task Modal */}
      <DelegatedTaskModal
        isOpen={showDelegatedTaskModal}
        data={delegatedTaskData}
        onCancel={handleDelegatedTaskCancel}
        onConfirm={handleDelegatedTaskConfirm}
        isLoading={isSubmitting}
      />

      {/* Precedent Conflict Modal */}
      <PrecedentConflictModal
        isOpen={showPrecedentConflictModal}
        taskName={currentTask?.name || formik.values.name}
        proposedStartDate={pendingPrecedentSubmitData?.values.startTime || ''}
        proposedEndDate={pendingPrecedentSubmitData?.values.endTime || ''}
        conflicts={precedentConflicts}
        onCancel={handlePrecedentConflictCancel}
        onConfirm={handlePrecedentConflictConfirm}
        isLoading={isSubmitting}
      />

      {/* Subtask Out of Range Modal */}
      <SubtaskOutOfRangeModal
        isOpen={showSubtaskOutOfRangeModal}
        data={subtaskOutOfRangeData}
        mode="move"
        onExtendParent={handleExtendParent}
        onLeaveOutside={handleLeaveSubtaskOutside}
        onCancel={handleSubtaskOutOfRangeCancel}
        isLoading={isSubmitting}
      />
    </SliderModal>
  );
};
